import { useState, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';
import { ClipLoader } from 'react-spinners';
import RocketLoader from '../components/RocketLoader';
import Head from 'next/head';
import Link from 'next/link';
import { UserContext } from '../components/Navigation';
import GoogleSignInButton from '../components/GoogleSignInButton';
import AuthLayout, { RoleToggle, Notice, Divider } from '../components/auth/AuthLayout';
import TextField from '../components/auth/TextField';
import { display } from '../lib/fonts';
import { btn, link, muted } from '../lib/ui';

export default function UnifiedLogin() {
  const router = useRouter();
  const { updateUserProfile } = useContext(UserContext);

  const [role, setRole] = useState('job-seeker');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [authChecked, setAuthChecked] = useState(false);

  // Read role from query param on mount
  useEffect(() => {
    if (router.query.role === 'referrer') {
      setRole('referrer');
    } else if (router.query.role === 'job-seeker') {
      setRole('job-seeker');
    }
  }, [router.query.role]);

  // Auth check on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/validate-token', {
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.authenticated && data.user) {
            updateUserProfile({
              name: data.user.name || data.user.email.split('@')[0],
              email: data.user.email,
              role: data.user.role,
              id: data.user.id
            });
            const dest = data.user.role === 'referrer' ? '/referrer-dashboard' : '/job-seeker-dashboard';
            router.push(dest);
            return;
          }
        }
      } catch (err) {
        console.error('Auth check failed:', err);
      } finally {
        setAuthChecked(true);
      }
    };
    checkAuth();
  }, [router, updateUserProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!email || !password) {
      setError('Please enter both email and password.');
      setLoading(false);
      return;
    }

    const endpoint = role === 'referrer' ? '/api/referrer-login' : '/api/job-seeker-login';
    const dashboard = role === 'referrer' ? '/referrer-dashboard' : '/job-seeker-dashboard';

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.message) {
          if (data.message.includes('Email not verified')) {
            setError('Email not verified. Redirecting to OTP verification...');
            localStorage.setItem('signupEmail', email);
            setTimeout(() => {
              router.push(`/verify-otp?email=${encodeURIComponent(email)}`);
            }, 2000);
          } else {
            setError(data.message);
          }
        } else {
          setError('Login failed. Please try again.');
        }
        setLoading(false);
        return;
      }

      if (data.success) {
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          window.location.href = dashboard;
        }, 500);
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (!authChecked) return <RocketLoader />;

  const isSeeker = role === 'job-seeker';

  const benefits = isSeeker
    ? [
        'Get direct referrals to top companies',
        'Practice with AI-powered mock interviews',
        'Personalized job matches for your skills',
        'Track applications in real-time',
        'Access exclusive job openings',
      ]
    : [
        'Post vacancies & find top talent',
        'Help professionals find dream opportunities',
        'Build your professional network',
        'Strengthen your company\'s team',
        'Make a meaningful career impact',
      ];

  return (
    <>
      <Head>
        <title>{isSeeker ? 'Job Seeker Login' : 'Referrer Login'} | Doogler</title>
        <meta name="description" content={isSeeker
          ? 'Login to your Doogler job seeker account. Get direct company referrals, practice with AI mock interviews, and track your applications.'
          : 'Login as a referrer to post open vacancies. Connect with high-quality candidates and help them find their perfect career path.'
        } />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`${isSeeker ? 'Job Seeker' : 'Referrer'} Login | Doogler`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://doogler.in/login" />
        <link rel="canonical" href="https://doogler.in/login" />
      </Head>

      <AuthLayout
        sideTitle={isSeeker ? 'Land your next role through a referral' : 'Hire people you can vouch for'}
        sideText={isSeeker
          ? 'Connect directly with employees at top companies and get insider access to opportunities.'
          : 'Post vacancies, refer talented professionals, and strengthen your company\'s workforce.'}
        benefits={benefits}
        image={isSeeker ? '/job_hunt.svg' : '/employee.svg'}
        imageAlt={isSeeker ? 'Job seeker illustration' : 'Referrer illustration'}
      >
        <h1 className={`${display.className} text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white`}>
          Welcome Back
        </h1>
        <p className={`mt-2 ${muted}`}>Sign in to your account to continue</p>

        <div className="mt-6">
          <RoleToggle role={role} onChange={(r) => { setRole(r); setError(''); }} />
        </div>

        {/* Preferred: Google sign-in */}
        <div className="mt-6">
          <GoogleSignInButton role={role} onError={setError} text="continue_with" />
        </div>

        <div className="my-6">
          <Divider>or continue with email</Divider>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <TextField
            id="login-email"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.com"
          />
          <TextField
            id="login-password"
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            placeholder="Your password"
          />

          <Notice type="error">{error}</Notice>
          <Notice type="success">{success}</Notice>

          <button type="submit" disabled={loading} className={`${btn.primary} w-full min-h-[48px]`}>
            {loading ? <ClipLoader size={20} color="#0b1220" /> : 'Sign In Now'}
          </button>
        </form>

        <p className={`mt-6 text-center ${muted}`}>
          Don&apos;t have an account?{' '}
          <Link href="/sign-up" className={link}>Sign Up</Link>
        </p>
      </AuthLayout>
    </>
  );
}
